import styled, {css, keyframes} from "styled-components";
import {ACTION_CONTROL_HEIGHT_PX, brand, fontFamilies, radii, type ModeTokens} from "theme/tokens";

/** Content-sized, but shrinkable inside the sender frame on short viewports. */
export const SuccessSurface = styled.div`
	display: flex;
	flex-direction: column;
	flex: 0 1 auto;
	min-height: 0;
`;

/** Keep content clear of the fold fade when everything fits. 10px, down from 18 (owner
 * 2026-09-11: too much dead space under the actions row) — the fade is subtler but the padding it
 * dictates is what the eye reads as the card's tail. */
const FOLD_FADE_PX = 10;

/**
 * A scroller's bottom fade: the last `px` of its box fade out, and as much padding lets the last line scroll clear
 * of the fade. The success card's own scroller uses it, and so does the live card's recipient list
 * (kitecorp/transferify-ui#3320), so the two fade alike. Mask alpha, not visible ink: opaque retains pixels and
 * transparent hides them, whatever the surface colour.
 */
export const bottomFade = (px: number) => css`
	padding-bottom: ${px}px;
	-webkit-mask-image: linear-gradient(to bottom, black calc(100% - ${px}px), transparent 100%);
	mask-image: linear-gradient(to bottom, black calc(100% - ${px}px), transparent 100%);
`;

/**
 * The success card's single scroll container.
 *
 * The stable gutter keeps the content from shifting when the card starts to scroll, but where the
 * platform draws classic scrollbars (Windows, Linux) that gutter is taken out of the CONTENT's width,
 * about 10px, while macOS's overlay scrollbars take nothing. The card's widths were chosen and measured
 * on the overlay case (360px beside the campaign, ui#2888), so on a classic-scrollbar machine every line
 * of the card had 10px less than it was measured with. `--success-scroll-gutter` is that gutter,
 * measured by `useScrollGutter`, and the negative end margin moves it into the card's own padding: the
 * content is the same width on every platform, and the scrollbar sits nearer the card's edge.
 */
export const SuccessScroll = styled.div`
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
	scrollbar-gutter: stable;
	scrollbar-width: thin;
	margin-inline-end: calc(-1 * var(--success-scroll-gutter, 0px));
	${bottomFade(FOLD_FADE_PX)};
`;

export const Root = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	/* 16px, down from 22 (owner 2026-09-11: the link-first card must sit comfortably in a 13"
	   viewport) — the four inter-section gaps carry most of the card's spare height. */
	gap: 11px;
	min-width: 0;
	/* Readable content stays above the decorative confetti. */
	& > :not([aria-hidden="true"]) { position: relative; z-index: 6; }
`;

const fall = keyframes`
	0% { transform: translateY(0) rotate(0); opacity: 1; }
	100% { transform: translateY(440px) rotate(720deg); opacity: 0; }
`;

export const ConfLayer = styled.div`
	position: absolute;
	inset: 0;
	pointer-events: none;
	overflow: hidden;
	z-index: 5;
	@media (prefers-reduced-motion: reduce) { display: none; }
`;

export const Conf = styled.span`
	position: absolute;
	top: -16px;
	border-radius: 2px;
	animation-name: ${fall};
	animation-timing-function: cubic-bezier(0.3, 0.6, 0.5, 1);
	animation-fill-mode: forwards;
`;

export interface ConfettiPiece {
	id: string;
	left: number;
	w: number;
	h: number;
	color: string;
	delay: number;
	dur: number;
}

export function confettiColors(tokens: ModeTokens): string[] {
	return [tokens.blue, tokens.teal, tokens.orange, brand.chipPurple, tokens.ink3];
}

export function buildConfetti(colors: string[]): ConfettiPiece[] {
	return Array.from({length: 30}).map((_, i) => ({
		id: `c${i}`,
		left: Math.round(Math.random() * 98),
		delay: +(Math.random() * 0.5).toFixed(2),
		dur: +(1.5 + Math.random() * 1.6).toFixed(2),
		w: 5 + Math.round(Math.random() * 6),
		h: 8 + Math.round(Math.random() * 10),
		color: colors[i % colors.length],
	}));
}

export const Title = styled.h2`
	margin: 0;
	font-weight: 700;
	/* 20px, down from 24 (owner 2026-09-12: shrink the download area on desktop). */
	font-size: 20px;
	line-height: 1.15;
	letter-spacing: -0.025em;
	color: ${({theme}) => theme.tokens.ink};
	@media (max-width: 480px) { font-size: 26px; }
`;

export const Sharing = styled.div`
	display: grid;
	gap: 8px;
	min-width: 0;
`;

const focusRing = css`
	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

export const CopyHint = styled.span<{$copied: boolean}>`
	position: absolute;
	right: 10px;
	top: 50%;
	translate: 0 -50%;
	font-size: 12px;
	font-weight: 600;
	padding: 2px 8px;
	border-radius: 999px;
	white-space: nowrap;
	color: ${({theme, $copied}) => $copied ? theme.tokens.actionInk : theme.tokens.linkInk};
	background: ${({theme, $copied}) => $copied ? theme.tokens.actionFill : theme.tokens.field};
	opacity: ${({$copied}) => $copied ? 1 : 0};
	transition: opacity 0.15s;
	@media (prefers-reduced-motion: reduce) { transition: none; }
`;

/**
 * The live card's "Copy", on show at all times (kitecorp/transferify-ui#3320, P3): the hover hint above never
 * appears on a phone. In the box's flow rather than over it, through its button ({@link CopyLinkButton}), so
 * the link's ellipsis stops before it.
 */
export const CopyPill = styled.span<{$copied: boolean}>`
	display: block;
	padding: 5px 11px;
	border-radius: 999px;
	font-size: 12.5px;
	font-weight: 600;
	white-space: nowrap;
	color: ${({theme, $copied}) => $copied ? theme.tokens.blueInk : theme.tokens.blue};
	background: ${({theme, $copied}) => $copied ? theme.tokens.blue : theme.tokens.card};
	box-shadow: ${({theme, $copied}) => $copied
		? "none"
		: `inset 0 0 0 1px color-mix(in srgb, ${theme.tokens.blue} 28%, ${theme.tokens.card})`};
`;

/** The link's box. Not the control itself: {@link CopyLinkButton} is, and covers it. */
export const LinkBox = styled.div<{$withPill?: boolean}>`
	position: relative;
	display: flex;
	align-items: center;
	width: 100%;
	min-width: 0;
	min-height: 44px;
	/* 8px vertical, down from 12 (owner 2026-09-12) — the 44px floor still sets the height. */
	padding: ${({$withPill}) => $withPill ? "8px 8px 8px 12px" : "8px 12px"};
	gap: 10px;
	background: ${({theme}) => theme.tokens.accentTint};
	border: 1px solid ${({theme}) => `color-mix(in srgb, ${theme.tokens.blue} 28%, ${theme.tokens.card})`};
	border-radius: 13px;
	color: ${({theme}) => theme.tokens.ink};
	@media (hover: hover) { &:hover ${CopyHint} { opacity: 1; } }
`;

/**
 * The box's ring, drawn where the box's own was (2px outside its 1px border) by a control that covers the
 * box from inside that border.
 */
const boxFocusRing = css`
	outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
	outline-offset: 3px;
	border-radius: 12px;
`;

/**
 * The copy control, which covers the WHOLE box while holding none of the link's text (see `ShareLinkBox`):
 * over the box for the hosted card, whose hint appears on hover or focus; for the live card it is the pill,
 * in the box's flow, and a pseudo-element stretches its press area and its focus ring over the box.
 */
export const CopyLinkButton = styled.button<{$pill: boolean}>`
	padding: 0;
	border: 0;
	background: none;
	font: inherit;
	color: inherit;
	cursor: pointer;
	&:focus-visible { outline: none; }
	&:focus-visible ${CopyHint} { opacity: 1; }
	${({$pill}) => $pill ? css`
		flex: none;
		margin-inline-start: auto;
		border-radius: 999px;
		&::after {
			content: "";
			position: absolute;
			inset: 0;
		}
		&:focus-visible::after { ${boxFocusRing}; }
	` : css`
		position: absolute;
		inset: 0;
		&:focus-visible { ${boxFocusRing}; }
	`}
`;

/** One line, always (owner 2026-09-11): a long link ends in an ellipsis rather than wrapping. */
export const ShareUrl = styled.span`
	min-width: 0;
	font: 600 13px/1.6 ${fontFamilies.sans};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

export const ExpiryPanel = styled.section`
	display: flex;
	flex-direction: column;
	gap: 5px;
	padding: 9px 18px;
	border-radius: ${radii.panel};
	background: ${({theme}) => `color-mix(in srgb, ${theme.tokens.card2} 40%, ${theme.tokens.card})`};
`;

export const ExpiryLine = styled.h3`
	display: flex;
	align-items: center;
	gap: 8px;
	margin: 0;
	font-size: 11px;
	line-height: 1.4;
	font-weight: 700;
	letter-spacing: 0.09em;
	text-transform: uppercase;
	color: ${({theme}) => theme.tokens.ink2};
	& > svg { flex: none; }
`;

export const ExpiryValue = styled.div`
	text-align: center;
	/* Slightly under 1em scales the boxed countdown tiles with it (their sizes are em-based). */
	font-size: 12px;
	font-weight: 700;
	color: ${({theme}) => theme.tokens.orange};
`;

export const Chips = styled.div`
	display: flex;
	justify-content: center;
	flex-wrap: wrap;
	gap: 7px;
`;

export const Achip = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 5px;
	background: ${({theme}) => theme.tokens.field};
	color: ${({theme}) => theme.tokens.ink2};
	font-size: 12px;
	font-weight: 600;
	padding: 6px 11px;
	border-radius: 9px;
`;

export const ActionsRow = styled.div`
	display: flex;
	gap: 10px;
`;

/** One box model keeps the primary and quiet actions equal, including wrapped translations. */
const actionControl = css`
	${focusRing};
	flex: 1;
	min-width: 0;
	min-height: ${ACTION_CONTROL_HEIGHT_PX}px;
	padding: 8px 10px;
	border-radius: ${radii.control};
	border: 1.5px solid transparent;
	font: 700 14px ${fontFamilies.sans};
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 7px;
	cursor: pointer;
	transition: 0.16s;
	& > svg { flex: none; }
	&:disabled { cursor: default; opacity: 0.6; }
	@media (prefers-reduced-motion: reduce) { transition: none; }
`;

export const BtnOut = styled.button`
	${actionControl};
	border-color: ${({theme}) => theme.tokens.fieldLine};
	background: ${({theme}) => theme.tokens.card};
	color: ${({theme}) => theme.tokens.ink};
	&:hover:not(:disabled) { background: ${({theme}) => theme.tokens.field}; }
`;

export const QuietButton = styled.button`
	${actionControl};
	background: transparent;
	color: ${({theme}) => theme.tokens.ink2};
	font-weight: 600;
	&:hover:not(:disabled) { background: ${({theme}) => theme.tokens.field}; }
`;

export const SubmitButton = styled.button`
	${actionControl};
	background: ${({theme}) => theme.tokens.actionFill};
	color: ${({theme}) => theme.tokens.actionInk};
	&:hover:not(:disabled) { background: ${({theme}) => theme.tokens.actionFillHover}; }
`;

export const Compose = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

/** Delivery failures remain visible after the toast disappears. */
export const SentNote = styled.div<{$warn?: boolean}>`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	font-size: 12.5px;
	font-weight: 700;
	color: ${({theme, $warn}) => ($warn ? theme.tokens.orange : theme.tokens.teal)};
	text-wrap: pretty;
`;
